import React, { useState, useRef, useCallback, useEffect, ReactNode } from 'react';
import { Button, ButtonProps, Overlay, Tooltip } from 'react-bootstrap';

const FEEDBACK_DURATION_MS = 1200;

const useCopy = (value: string) => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  const copy = useCallback(async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      await navigator.clipboard?.writeText(value);
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), FEEDBACK_DURATION_MS);
    } catch {
      // ignore
    }
  }, [value]);

  return { copied, copy };
};

type CopyableTextElement = 'span' | 'code' | 'div';

interface CopyableTextProps {
  value: string;
  children?: ReactNode;
  as?: CopyableTextElement;
  className?: string;
  title?: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
}

export const CopyableText: React.FC<CopyableTextProps> = ({
  value,
  children,
  as = 'span',
  className,
  title,
  placement = 'top',
}) => {
  const { copied, copy } = useCopy(value);
  const ref = useRef<HTMLElement>(null);

  const element = React.createElement(
    as,
    {
      ref,
      className,
      title: title ?? 'Click to copy',
      style: { cursor: 'pointer' },
      onClick: copy,
    },
    children ?? value,
  );

  return (
    <>
      {element}
      <Overlay target={ref.current} show={copied} placement={placement}>
        {(props) => <Tooltip {...props}>Copied!</Tooltip>}
      </Overlay>
    </>
  );
};

interface CopyButtonProps extends Omit<ButtonProps, 'onClick'> {
  value: string;
  children?: ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right';
}

export const CopyButton: React.FC<CopyButtonProps> = ({
  value,
  children,
  title,
  placement = 'top',
  ...buttonProps
}) => {
  const { copied, copy } = useCopy(value);
  const ref = useRef<HTMLButtonElement>(null);

  return (
    <>
      <Button
        ref={ref}
        title={title ?? 'Copy to clipboard'}
        onClick={copy}
        {...buttonProps}
      >
        {children ?? <i className="bi bi-clipboard"></i>}
      </Button>
      <Overlay target={ref.current} show={copied} placement={placement}>
        {(props) => <Tooltip {...props}>Copied!</Tooltip>}
      </Overlay>
    </>
  );
};
